<script setup lang="ts">
import type { UpdateProfileParams } from '@/api/modules/user'
import { toTypedSchema } from '@vee-validate/zod'
import { ElMessage } from 'element-plus'
import { useForm } from 'vee-validate'
import * as z from 'zod'
import ImageUpload from '@/components/ImageUpload/index.vue'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/shadcn/ui/form'

defineOptions({
  name: 'EditProfileForm',
})

const userStore = useUserStore()

// 上传配置 - 公共上传接口不包含 /admin/ 前缀
const uploadAction = `${'https://erp-api.wenlewen.com/api/admin/v1/'.replace('admin/', '')}common/upload`
const uploadHeaders = computed(() => ({
  Authorization: `Bearer ${userStore.token}`,
}))
const uploadData = {
  platform: 'desktop',
  file_type: 'image',
  user_type: 'admin',
  is_material: false,
}

const loading = ref(false)

// 用户名验证：3-50位，只能包含字母、数字、下划线
const usernameRegex = /^\w{3,50}$/
// 手机号验证：11位，1开头
const phoneRegex = /^1\d{10}$/
// 邮箱验证
const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

const form = useForm({
  validationSchema: toTypedSchema(
    z.object({
      username: z.string()
        .min(1, '请输入用户名')
        .min(3, '用户名长度为3-50位')
        .max(50, '用户名长度为3-50位')
        .regex(usernameRegex, '用户名只能包含字母、数字、下划线'),
      name: z.string()
        .min(1, '请输入姓名')
        .max(50, '姓名最多50个字符'),
      phone: z.string()
        .min(1, '请输入手机号')
        .regex(phoneRegex, '请输入正确的手机号'),
      email: z.string()
        .optional()
        .refine(val => !val || emailRegex.test(val), '请输入正确的邮箱格式'),
      gender: z.number().optional(),
      avatar: z.string().max(255, '头像URL最多255个字符').optional(),
      remark: z.string().max(1000, '备注最多1000个字符').optional(),
    }),
  ),
  initialValues: {
    username: userStore.userInfo?.username || '',
    name: userStore.userInfo?.name || '',
    phone: userStore.userInfo?.phone || '',
    email: userStore.userInfo?.email || '',
    gender: userStore.userInfo?.gender || 0,
    avatar: userStore.userInfo?.avatar || '',
    remark: userStore.userInfo?.remark || '',
  },
})

// 头像上传成功回调
function handleAvatarSuccess(res: any) {
  if (res.code === 0 && res.data?.url) {
    form.setFieldValue('avatar', res.data.url)
    ElMessage.success('头像上传成功')
  }
  else {
    ElMessage.error(res.message || '头像上传失败')
  }
}

const onSubmit = form.handleSubmit(async (values) => {
  loading.value = true
  try {
    const userId = userStore.userId
    if (userId === null) {
      throw new Error('用户信息不存在，请重新登录')
    }
    await userStore.updateProfile(userId, values as UpdateProfileParams)
    ElMessage.success('个人信息更新成功')
  }
  catch (error: any) {
    ElMessage.error(error.message || '个人信息更新失败')
  }
  finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="w-full flex-col-stretch-center">
    <div class="mb-4 space-y-1">
      <h3 class="text-base color-[var(--el-text-color-primary)] font-medium">
        编辑个人信息
      </h3>
      <p class="text-xs text-muted-foreground">
        更新您的账号基本信息
      </p>
    </div>
    <form @submit="onSubmit">
      <FormField v-slot="{ componentField, errors }" name="username">
        <FormItem class="relative pb-4 space-y-1">
          <FormLabel class="text-xs font-medium">
            用户名
          </FormLabel>
          <FormControl>
            <FaInput readonly disabled placeholder="用户名" class="h-8 w-full cursor-not-allowed bg-muted text-xs" :class="errors.length && 'border-destructive'" v-bind="componentField" />
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-0 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errors }" name="name">
        <FormItem class="relative pb-4 space-y-1">
          <FormLabel class="text-xs font-medium">
            姓名
          </FormLabel>
          <FormControl>
            <FaInput placeholder="请输入姓名" class="h-8 w-full text-xs" :class="errors.length && 'border-destructive'" v-bind="componentField" />
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-0 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errors }" name="phone">
        <FormItem class="relative pb-4 space-y-1">
          <FormLabel class="text-xs font-medium">
            手机号
          </FormLabel>
          <FormControl>
            <FaInput placeholder="请输入手机号" class="h-8 w-full text-xs" :class="errors.length && 'border-destructive'" v-bind="componentField" />
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-0 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errors }" name="email">
        <FormItem class="relative pb-4 space-y-1">
          <FormLabel class="text-xs font-medium">
            邮箱
          </FormLabel>
          <FormControl>
            <FaInput placeholder="请输入邮箱（可选）" class="h-8 w-full text-xs" :class="errors.length && 'border-destructive'" v-bind="componentField" />
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-0 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errors }" name="gender">
        <FormItem class="relative pb-6 space-y-2">
          <FormLabel class="text-sm font-medium">
            性别
          </FormLabel>
          <FormControl>
            <select
              :value="componentField.modelValue"
              class="h-10 w-full border border-input rounded-md bg-background px-3"
              :class="errors.length && 'border-destructive'"
              @change="(e) => componentField.onChange(Number((e.target as HTMLSelectElement).value))"
            >
              <option :value="0">
                未知
              </option>
              <option :value="1">
                男
              </option>
              <option :value="2">
                女
              </option>
            </select>
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-1 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="avatar">
        <FormItem class="relative pb-6 space-y-2">
          <FormLabel class="text-sm font-medium">
            头像
          </FormLabel>
          <FormControl>
            <ImageUpload
              v-model="componentField.modelValue"
              :action="uploadAction"
              :headers="uploadHeaders"
              :data="uploadData"
              :width="120"
              :height="120"
              :size="2"
              notip
              @on-success="handleAvatarSuccess"
            />
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-1 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errors }" name="remark">
        <FormItem class="relative pb-6 space-y-2">
          <FormLabel class="text-sm font-medium">
            备注
          </FormLabel>
          <FormControl>
            <textarea
              class="min-h-20 w-full resize-y border border-input rounded-md bg-background px-3 py-2"
              :class="errors.length && 'border-destructive'"
              placeholder="请输入备注（可选）"
              v-bind="componentField"
            />
          </FormControl>
          <Transition enter-active-class="transition-opacity" enter-from-class="opacity-0" leave-active-class="transition-opacity" leave-to-class="opacity-0">
            <FormMessage class="absolute bottom-1 text-xs" />
          </Transition>
        </FormItem>
      </FormField>

      <FaButton :loading="loading" size="lg" class="mt-8 w-full" type="submit">
        保存
      </FaButton>
    </form>
  </div>
</template>
